import { toHarnessPick, type Roster, type RosterPlayer } from './roster';
import { FIRST_BENCH_SLOT, type HarnessPick, type HarnessSquad } from './squad';
import type { FplPosition } from '@lib/live/team/types';

/**
 * The XI this fills to, and the order slots are handed out in.
 *
 * A flat 4-4-2 with the conventional bench. Any legal formation would do; this
 * one is picked because it is the middle of the range, so adjusting it towards
 * whatever scenario is wanted takes the fewest swaps.
 */
const STARTER_SHAPE: readonly { position: FplPosition; count: number }[] = [
  { position: 'GK', count: 1 },
  { position: 'DEF', count: 4 },
  { position: 'MID', count: 4 },
  { position: 'FWD', count: 2 }
];

const BENCH_SHAPE: readonly FplPosition[] = ['GK', 'DEF', 'MID', 'FWD'];

/**
 * Players who have actually taken part come first.
 *
 * A squad filled from whoever happened to be listed first is mostly unused
 * substitutes, which renders a pitch of zeroes and proves nothing. Ordering by
 * minutes and then points puts a scoring team on the pitch straight away,
 * which is the useful starting point to adjust from.
 */
const byContribution = (a: RosterPlayer, b: RosterPlayer): number =>
  b.minutesPlayed - a.minutesPlayed || b.points - a.points;

/** Deduplicated across fixtures: a player in both legs of a double is one pick. */
const poolFrom = (rosters: Roster[]): RosterPlayer[] => {
  const byOptaId = new Map<string, RosterPlayer>();
  for (const roster of rosters) {
    for (const player of roster.players) {
      const existing = byOptaId.get(player.optaId);
      if (!existing || byContribution(player, existing) < 0) {
        byOptaId.set(player.optaId, player);
      }
    }
  }
  return Array.from(byOptaId.values()).sort(byContribution);
};

export type FillResult =
  | { squad: HarnessSquad }
  | { shortfall: Record<FplPosition, number> };

const SQUAD_SHAPE: Record<FplPosition, number> = {
  GK: 2,
  DEF: 5,
  MID: 5,
  FWD: 3
};

/**
 * A legal fifteen drawn from the selected fixtures, ready to adjust.
 *
 * This is the starting point, not the mechanism. The reason the harness lets a
 * squad be chosen by hand is to reach branches a filled one will not: a
 * captain who never comes on, a blanked starter with a playable substitute, a
 * blanked defender with only a midfielder benched. Filling just removes the
 * tedium of getting to a legal squad before any of that can be set up.
 *
 * @returns the squad, or what the selected fixtures are short of. A single
 * match usually cannot fill a squad on its own: both sides together carry
 * enough outfield players, but rarely enough of every position once the
 * unidentified ones are excluded.
 */
export const fillSquadFromRosters = (
  rosters: Roster[],
  gameweek: number
): FillResult => {
  const pool = poolFrom(rosters);
  const available: Record<FplPosition, RosterPlayer[]> = {
    GK: pool.filter((one) => one.position === 'GK'),
    DEF: pool.filter((one) => one.position === 'DEF'),
    MID: pool.filter((one) => one.position === 'MID'),
    FWD: pool.filter((one) => one.position === 'FWD')
  };

  const shortfall: Record<FplPosition, number> = {
    GK: 0,
    DEF: 0,
    MID: 0,
    FWD: 0
  };
  let isShort = false;
  for (const [position, needed] of Object.entries(SQUAD_SHAPE)) {
    const missing = needed - available[position as FplPosition].length;
    if (missing > 0) {
      shortfall[position as FplPosition] = missing;
      isShort = true;
    }
  }
  if (isShort) return { shortfall };

  const taken: Record<FplPosition, number> = { GK: 0, DEF: 0, MID: 0, FWD: 0 };
  const take = (position: FplPosition): RosterPlayer => {
    const player = available[position][taken[position]];
    taken[position] += 1;
    return player;
  };

  const picks: HarnessPick[] = [];
  let slot = 1;
  for (const { position, count } of STARTER_SHAPE) {
    for (let index = 0; index < count; index += 1) {
      picks.push(toHarnessPick(take(position), slot, rosters));
      slot += 1;
    }
  }
  for (const position of BENCH_SHAPE) {
    picks.push(toHarnessPick(take(position), slot, rosters));
    slot += 1;
  }

  // Picks are built in slot order out of a pool already sorted by
  // contribution, so the first starter in a position is its best contributor.
  const firstStarterSlot = (position: FplPosition): number =>
    picks.find(
      (pick) => pick.slot < FIRST_BENCH_SLOT && pick.position === position
    )?.slot ?? 1;

  return {
    squad: {
      gameweek,
      picks,
      // The armbands start on the leading forward and midfielder, which is
      // both what a manager would do and what makes a captaincy change
      // visible when one of them is swapped for somebody who blanked.
      captainSlot: firstStarterSlot('FWD'),
      viceCaptainSlot: firstStarterSlot('MID'),
      activeChip: null
    }
  };
};
